import React from 'react';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';

const ServiceCTA = () => {
  const ctaOptions = [
    {
      title: 'Schedule Consultation',
      description: 'Book a comprehensive eye examination with our specialists.',
      icon: 'Calendar',
      action: 'Book Now',
      variant: 'default',
      onClick: () => (window.location.href = '/book-appointment-guided-care-journey'),
      popular: true,
    },
    {
      title: 'Call Now',
      description: 'Speak directly with our customer care for any inquiries.',
      icon: 'Phone',
      action: 'Call Now',
      variant: 'primary',
      onClick: () => (window.location.href = 'tel:+254718202020'),
      popular: false,
    },
    {
      title: 'Walk-In Care',
      description: 'Visit any of our branches for immediate, in-person care.',
      icon: 'MapPin',
      action: 'Find Location',
      variant: 'outline',
      onClick: () => (window.location.href = '/locations-community-health-hub-network'),
      popular: false,
    },
  ];

  const benefits = [
    {
      icon: 'Shield',
      title: 'Insurance Accepted',
      description: 'We work with all major insurance providers.',
    },
    {
      icon: 'Clock',
      title: 'Same Day Appointments',
      description: 'Urgent cases seen within 24 hours.',
    },
    {
      icon: 'Award',
      title: 'Expert Specialists',
      description: 'Board-certified ophthalmologists.',
    },
    {
      icon: 'Heart',
      title: 'Compassionate Care',
      description: 'Patient-centered approach to treatment.',
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-[#eb2227]/5 via-white to-[#08005f]/5">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Title */}
        <div className="text-center mb-16">
          <h2 className="font-heading font-bold text-3xl lg:text-4xl text-[#08005f] mb-4">
            Ready to Protect Your Vision?
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Take the first step towards better eye health. Our team of specialists is ready to provide
            you with world-class care tailored to your unique needs.
          </p>
        </div>

        {/* Main CTA Options */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {ctaOptions.map((option, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition ${
                option.popular ? 'border-2 border-[#eb2227]' : 'border border-gray-200'
              }`}
            >
              {option.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#eb2227] text-white px-4 py-1 rounded-full text-sm font-medium shadow">
                    Most Popular
                  </div>
                </div>
              )}

              <div
                className={`w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 ${
                  option.variant === 'default'
                    ? 'bg-[#eb2227]/10'
                    : option.variant === 'destructive'
                    ? 'bg-red-50'
                    : 'bg-[#08005f]/10'
                }`}
              >
                <Icon
                  name={option.icon}
                  size={28}
                  color={
                    option.variant === 'default'
                      ? '#eb2227'
                      : option.variant === 'destructive'
                      ? '#eb2227'
                      : '#08005f'
                  }
                />
              </div>

              <h3 className="font-heading font-bold text-xl text-[#08005f] mb-3">
                {option.title}
              </h3>
              <p className="text-gray-600 mb-6">{option.description}</p>

              <Button
                variant={option.variant}
                fullWidth
                className={`${
                  option.variant === 'default'
                    ? 'bg-[#08005f] hover:bg-[#eb2227] text-white'
                    : option.variant === 'outline'
                    ? 'border-[#08005f] text-[#08005f] hover:bg-[#eb2227] hover:text-white'
                    : 'bg-[#08005f] hover:bg-[#eb2227] text-white'
                }`}
                iconName={option.icon}
                iconPosition="left"
                iconSize={16}
                onClick={option.onClick}
              >
                {option.action}
              </Button>
            </div>
          ))}
        </div>

        {/* Benefits Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h3 className="font-heading font-bold text-2xl text-[#08005f] text-center mb-8">
            Why Choose Eye&U Kenya?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-4 rounded-lg hover:bg-[#eb2227]/5 transition"
              >
                <div className="w-12 h-12 bg-[#eb2227]/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Icon name={benefit.icon} size={20} color="#eb2227" />
                </div>
                <h4 className="font-heading font-semibold text-[#08005f] mb-2">
                  {benefit.title}
                </h4>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="grid md:grid-cols-2 gap-8 mt-12">
          {/* Locations */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="font-heading font-bold text-lg text-[#08005f] mb-4 flex items-center">
              <Icon name="MapPin" size={20} className="mr-2 text-[#eb2227]" />
              Visit Our Clinics
            </h4>
            <div className="space-y-4">
              <div>
                <h5 className="font-semibold text-[#08005f]">Westlands Main Branch</h5>
                <p className="text-sm text-gray-600">3rd Floor, Pacis Centre, Waiyaki Way, Westlands, Nairobi</p>
                <p className="text-sm text-gray-600">Mon-Fri: 8:30AM-5:00PM, Sat: 8:30AM-1:00PM</p>
              </div>
              {/*<div>
                <h5 className="font-semibold text-[#08005f]">Eastleigh Branch</h5>
                <p className="text-sm text-gray-600">
                  Minhaj Towers, Ground Floor, Suite No. 08, Nairobi
                </p>
                <p className="text-sm text-gray-600">Mon-Fri: 9:30AM-5:00PM, Sat: 10:00AM-3:00PM</p>
              </div>*/}
              <div>
                <h5 className="font-semibold text-[#08005f]">Nakuru Branch</h5>
                <p className="text-sm text-gray-600">Ground Floor, Assumption Centre, Moi Road, Nakuru</p>
                <p className="text-sm text-gray-600">Mon-Fri: 9:30AM-5:00PM,  Sat: 10:00AM-3:00PM</p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h4 className="font-heading font-bold text-lg text-[#08005f] mb-4 flex items-center">
              <Icon name="Phone" size={20} className="mr-2 text-[#eb2227]" />
              Contact Information
            </h4>
            <div className="space-y-3">
              <div
                className="flex items-center space-x-3 cursor-pointer"
                onClick={() => (window.location.href = 'tel:+254718202020')}
              >
                <Icon name="Phone" size={16} className="text-[#eb2227]" />
                <div>
                  <p className="font-semibold text-[#08005f]">+254 718 202 020</p>
                  <p className="text-sm text-gray-600">Main appointment line</p>
                </div>
              </div>
             {/* <div
                className="flex items-center space-x-3 cursor-pointer"
                onClick={() => (window.location.href = 'https://wa.me/254745202020')}
              >
                <Icon name="MessageCircle" size={16} className="text-[#eb2227]" />
                <div>
                  <p className="font-semibold text-[#08005f]">+254 745 202 020</p>
                  <p className="text-sm text-gray-600">WhatsApp support</p>
                </div>
              </div>*/}
              <div
                className="flex items-center space-x-3 cursor-pointer"
                onClick={() => (window.location.href = 'mailto:info@eyenu.com')}
              >
                <Icon name="Mail" size={16} className="text-[#eb2227]" />
                <div>
                  <p className="font-semibold text-[#08005f]">info@eyenu.com</p>
                  <p className="text-sm text-gray-600">General inquiries</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceCTA;
